<template>
  <div class="order-details">
    <!-- 顶部操作栏 -->
    <div class="action-bar">
      <div class="left-actions">
        <el-button icon="el-icon-back" size="medium" plain @click="goBack">返回列表</el-button>
      </div>
      <div class="date-info">
        <div v-if="creatorInfo">创建时间：{{ creatorInfo.username || orderData.nickname || '用户' }} {{ formatDateTime(orderData.created_at) }}</div>
        <div v-if="updaterInfo.username">上次更新：{{ updaterInfo.username }} {{ formatDateTime(orderData.updated_at) }}</div>
      </div>
      <div class="right-actions">
        <div class="user-id-wrapper">
          <template v-if="!editingUserId">
            <el-tag type="info" @dblclick.native="!orderData.user_id ? startEditUserId() : null" :disable-transitions="true">User ID: {{ orderData.user_id || '- - - - -' }}</el-tag>
          </template>
          <template v-else>
            <el-input v-model="userIdEdit.user_id" size="small" placeholder="输入User ID" class="user-id-input">
              <template #append>
                <el-button icon="el-icon-check" @click="saveUserId"></el-button>
                <el-button icon="el-icon-close" @click="cancelEditUserId"></el-button>
              </template>
            </el-input>
          </template>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <div class="content-grid">
        <!-- 左侧内容列 -->
        <div class="left-column">
          <!-- 订单基本信息卡片 -->
          <el-card class="order-info-card">
            <div class="card-header">
              <span>订单信息</span>
            </div>
            <div class="info-grid">
              <div class="info-section">
                <div class="info-item">
                  <span class="label">订单编号</span>
                  <span class="value">{{ orderData.order_no || '-' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">用户昵称</span>
                  <span class="value">{{ orderData.nickname || orderData.nickname || '-' }}</span>
                </div>
                <div class="info-item" v-if="orderData.referral_code && orderData.referral_code !== 'ATLS2021'">
                  <span class="label">推荐码</span>
                  <span class="value">{{ orderData.referral_code }}</span>
                </div>
                <div class="info-item">
                  <span class="label">报名人数</span>
                  <span class="value">{{ orderData.participants_count || '0' }} 人</span>
                </div>
              </div>
              <div class="info-section">
                <div class="info-item">
                  <span class="label">创建方式</span>
                  <span class="value">{{ orderData.is_manual ? '员工创建' : '用户报名' }}</span>
                </div>
                <div class="info-item">
                  <span class="label">绑定手机号</span>
                  <span class="value">{{ orderData.phone || '-' }}</span>
                </div>
                <div class="info-item" v-if="orderData.referrer_name && orderData.referral_code !== 'ATLS2021'">
                  <span class="label">推荐方</span>
                  <span class="value">{{ orderData.referrer_name }}</span>
                </div>
                <div class="info-item">
                  <span class="label">订单金额</span>
                  <span class="value amount">{{ orderData.total_amount }}</span>
                </div>
              </div>
            </div>
          </el-card>

          <!-- 活动信息卡片 -->
          <el-card class="activity-info-card">
            <div class="card-header">
              <span>{{ orderData.title_cn || '关联活动' }}</span>
              <div class="header-actions">
                <el-button type="text" icon="el-icon-view" @click="viewActivity(orderData.activity_id)">活动详情</el-button>
              </div>
            </div>
            <div class="activity-participants">
              <el-row :gutter="20">
                <el-col v-for="(participant, idx) in orderData.participants" :key="idx" :span="24">
                  <el-card class="desc-participant-card">
                    <div class="desc-title" @click="goToParticipantDetail">
                      <span class="desc-title-bar"></span>
                      <span>报名人{{ idx + 1 }}</span>
                    </div>
                    <el-descriptions :column="isMobile ? 1 : 3" size="small" border>
                      <el-descriptions-item label="姓名">{{ participant.name || '- - -' }}</el-descriptions-item>
                      <el-descriptions-item label="性别">{{ formatGender(participant.gender) }}</el-descriptions-item>
                      <el-descriptions-item label="年龄">{{ participant.age || '-' }}岁</el-descriptions-item>
                      <el-descriptions-item label="手机号">{{ participant.phone || '- - -' }}</el-descriptions-item>
                      <el-descriptions-item label="身份证号">{{ participant.id_card_number || '- - -' }}</el-descriptions-item>
                    </el-descriptions>
                  </el-card>
                </el-col>
                <el-col v-if="!orderData.participants || orderData.participants.length === 0" :span="24">
                  <el-card class="desc-participant-card">
                    <div class="desc-title"><span class="desc-title-bar"></span>报名人</div>
                    <div class="desc-unfilled">
                      <i class="el-icon-warning-outline"></i>
                      <span>未填写报名人信息</span>
                    </div>
                  </el-card>
                </el-col>
              </el-row>
            </div>
          </el-card>
        </div>

        <!-- 右侧内容列 -->
        <div class="right-column">
          <!-- 费用概述卡片 -->
          <el-card class="cost-overview-card">
            <div class="card-header">
              <span>费用概述</span>
              <div class="header-actions">
                <template v-if="!editingPayment">
                  <el-button type="text" icon="el-icon-edit" @click="startEditPayment">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="savePayment">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditPayment">取消</el-button>
                </template>
              </div>
            </div>
            <div class="cost-overview">
              <template v-if="!editingPayment">
                <div class="cost-grid">
                  <div class="cost-item">
                    <span class="label">订单状态</span>
                    <el-tag :type="getStatusType(orderData.order_status)">{{ getStatusText(orderData.order_status) }}</el-tag>
                  </div>
                  <div class="cost-item">
                    <span class="label">需支付</span>
                    <span class="value payable">{{ formatPrice(orderData.payable_amount) }}</span>
                  </div>
                  <div class="cost-item" v-if="orderData.locked_discount_bonus && orderData.locked_discount_bonus > 0">
                    <span class="label">优惠金额<span class="label-note">（锁定金额为准）</span></span>
                    <span class="value discount">
                      <template v-if="(orderData.participants_count || 1) > 1">
                        {{ formatPrice(orderData.locked_discount_bonus) }} × {{ orderData.participants_count || 1 }} = {{ formatPrice(orderData.locked_discount_bonus * (orderData.participants_count || 1)) }}
                      </template>
                      <template v-else>
                        {{ formatPrice(orderData.locked_discount_bonus) }}
                      </template>
                    </span>
                  </div>
                  <div class="cost-item" v-if="orderData.referral_code_discount_amount && orderData.referral_code_discount_amount > 0">
                    <span class="label">推荐码折扣金额</span>
                    <span class="value discount">
                      <template v-if="(orderData.participants_count || 1) > 1">
                        {{ formatPrice(orderData.referral_code_discount_amount) }} × {{ orderData.participants_count || 1 }} = {{ formatPrice(orderData.referral_code_discount_amount * (orderData.participants_count || 1)) }}
                      </template>
                      <template v-else>
                        {{ formatPrice(orderData.referral_code_discount_amount) }}
                      </template>
                    </span>
                  </div>
                  <div class="cost-item">
                    <span class="label">已付金额</span>
                    <span class="value paid">{{ formatPrice(orderData.paid_amount) }}</span>
                  </div>
                  <div class="cost-item" v-if="orderData.refund_amount && orderData.refund_amount > 0">
                    <span class="label">退款金额</span>
                    <span class="value refund">{{ formatPrice(orderData.refund_amount) }}</span>
                  </div>
                  <div class="cost-item" v-if="orderData.refund_reason">
                    <span class="label">退款原因</span>
                    <span class="value">{{ orderData.refund_reason }}</span>
                  </div>
                  <div class="cost-item" v-if="orderData.payment_expire_at && orderData.paid_amount === 0">
                    <span class="label">过期时间</span>
                    <span class="value">{{ formatDateTime(orderData.payment_expire_at) }}</span>
                  </div>
                  <div class="cost-item" v-if="orderData.admin_remark">
                    <span class="label">管理员备注</span>
                    <span class="value">{{ orderData.admin_remark }}</span>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="payment-form">
                  <el-form :model="paymentEdit" label-width="100px">
                    <el-form-item label="支付状态">
                      <el-select v-model="paymentEdit.order_status">
                        <el-option :value="0" label="待支付"></el-option>
                        <el-option :value="1" label="订单取消"></el-option>
                        <el-option :value="10" label="待支付定金"></el-option>
                        <el-option :value="11" label="已支付定金"></el-option>
                        <el-option :value="20" label="待支付尾款"></el-option>
                        <el-option :value="21" label="已支付尾款"></el-option>
                        <el-option :value="30" label="待支付全款"></el-option>
                        <el-option :value="31" label="已支付全款"></el-option>
                        <el-option :value="40" label="待支付交通费"></el-option>
                        <el-option :value="41" label="已支付交通费"></el-option>
                        <el-option :value="50" label="定金已付，待付尾款"></el-option>
                        <el-option :value="51" label="定金尾款已付"></el-option>
                        <el-option :value="100" label="订单完成"></el-option>
                        <el-option :value="101" label="部分退款"></el-option>
                        <el-option :value="102" label="全额退款"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="折扣金额">
                      <div style="display:flex;align-items:center;gap:8px;">
                        <el-input-number 
                          v-model="paymentEdit.locked_discount_bonus"
                          :min="0"
                          :precision="2"
                          :step="0.01">
                        </el-input-number>
                        <span v-if="(orderData.participants_count || 1) > 1" class="value discount">
                          × {{ orderData.participants_count || 1 }} = {{ formatPrice((paymentEdit.locked_discount_bonus || 0) * (orderData.participants_count || 1)) }}
                        </span>
                      </div>
                    </el-form-item>
                    <el-form-item label="支付方式">
                      <el-select v-model="paymentEdit.payment_method">
                        <el-option :value="1" label="微信小程序"></el-option>
                        <el-option :value="2" label="支付宝"></el-option>
                        <el-option :value="3" label="现金"></el-option>
                        <el-option :value="4" label="银行转账"></el-option>
                      </el-select>
                    </el-form-item>
                    <el-form-item label="交易流水号">
                      <el-input v-model="paymentEdit.transaction_id"></el-input>
                    </el-form-item>
                    <el-form-item label="支付凭证">
                      <div class="evidence-preview" v-if="paymentEdit.payment_evidence_url">
                        <img id="payment-edit-img" :src="getImageUrl(paymentEdit.payment_evidence_url, 'payment-edit-img')" alt="支付凭证">
                      </div>
                      <el-upload
                        class="evidence-uploader"
                        :action="getUploadUrl('payment_info')"
                        :headers="getHeaders()"
                        :show-file-list="false"
                        :before-upload="beforeUpload"
                        :on-success="(res) => handleUploadSuccess(res, 'payment')">
                        <el-button size="small" type="primary">
                          {{ paymentEdit.payment_evidence_url ? '替换照片' : '点击上传' }}
                        </el-button>
                        <div slot="tip" class="el-upload__tip">只能上传jpg/png文件，且不超过10MB</div>
                      </el-upload>
                    </el-form-item>
                    <el-form-item label="管理员备注">
                      <el-input
                        type="textarea"
                        v-model="paymentEdit.admin_remark"
                        :rows="3"
                        placeholder="请输入管理员备注">
                      </el-input>
                    </el-form-item>
                  </el-form>
                </div>
              </template>
            </div>
          </el-card>


          <!-- 支付记录列表卡片 -->
          <el-card class="payment-records-card" v-if="orderData.payments && orderData.payments.length > 0">
            <div class="card-header">
              <span>支付记录 ({{ orderData.payments.length }})</span>
            </div>
            <div class="payment-records-content">
              <div class="payment-record" v-for="payment in orderData.payments" :key="payment.id">
                <div class="record-header">
                  <div class="record-title">
                    <span class="record-number">{{ getPaymentType(payment.payment_type) }}</span>
                    <el-tag :type="getPaymentRecordStatusType(payment.status)" size="small">
                      {{ getPaymentRecordStatusText(payment.status, payment.payment_type) }}
                  </el-tag>
                </div>
                  <div class="record-amount">
                    <div v-if="payment.payment_type === 4 && orderData.transportation_type === 4" class="no-payment-container">
                      <span class="no-payment-text">无需支付</span>
                    </div>
                    <span class="amount-value" v-else>{{ formatPrice(payment.amount) }}</span>
                </div>
                </div>
                <div class="record-details" v-if="payment.paid_at || payment.error_code || payment.error_message || (payment.payment_type === 4 && orderData.transportation_type)">
                  <div class="detail-grid">
                    <div class="detail-item" v-if="payment.paid_at">
                      <span class="label">支付时间</span>
                      <span class="value">{{ formatDateTime(payment.paid_at) }}</span>
                    </div>
                    <div class="detail-item" v-if="payment.payment_type === 4 && orderData.transportation_type">
                      <span class="label">交通方式</span>
                      <span class="value">{{ getTransportationType(orderData.transportation_type) }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </el-card>




          <!-- 退款信息卡片 -->
          <el-card class="refund-card" v-if="orderData.order_status === 101">
            <div class="card-header">
              <span>退款信息</span>
              <div class="header-actions">
                <template v-if="!editingRefund">
                  <el-button type="text" icon="el-icon-edit" @click="startEditRefund">编辑</el-button>
                </template>
                <template v-else>
                  <el-button type="text" icon="el-icon-check" @click="saveRefund">保存</el-button>
                  <el-button type="text" icon="el-icon-close" @click="cancelEditRefund">取消</el-button>
                </template>
              </div>
            </div>
            <div class="refund-info">
              <template v-if="!editingRefund">
                <div class="refund-grid">
                  <div class="refund-item">
                    <span class="label">退款金额</span>
                    <span class="value price">{{ formatPrice(orderData.refund_amount) }}</span>
                  </div>
                  <div class="refund-item">
                    <span class="label">退款完成时间</span>
                    <span class="value">{{ formatDateTime(orderData.refund_completed_at) || '-' }}</span>
                  </div>
                  <div class="refund-item full-width">
                    <span class="label">退款原因</span>
                    <span class="value">{{ orderData.refund_reason || '-' }}</span>
                  </div>
                  <div class="refund-item full-width" v-if="orderData.refund_evidence_url">
                    <span class="label">退款凭证</span>
                    <div class="evidence-image">
                      <img id="refund-img" :src="getImageUrl(orderData.refund_evidence_url, 'refund-img')" alt="加载失败">
                    </div>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="refund-form">
                  <el-form :model="refundEdit" label-width="100px">
                    <el-form-item label="退款金额">
                      <el-input-number 
                        v-model="refundEdit.refund_amount"
                        :min="0"
                        :precision="2"
                        :step="0.01">
                      </el-input-number>
                    </el-form-item>
                    <el-form-item label="退款原因">
                      <el-input 
                        type="textarea"
                        v-model="refundEdit.refund_reason"
                        :rows="3">
                      </el-input>
                    </el-form-item>
                    <el-form-item label="退款凭证">
                      <div class="evidence-preview" v-if="refundEdit.refund_evidence_url">
                        <img id="refund-edit-img" :src="getImageUrl(refundEdit.refund_evidence_url, 'refund-edit-img')" alt="退款凭证">
                      </div>
                      <el-upload
                        class="evidence-uploader"
                        :action="getUploadUrl('payment_info')"
                        :headers="getHeaders()"
                        :show-file-list="false"
                        :before-upload="beforeUpload"
                        :on-success="(res) => handleUploadSuccess(res, 'refund')">
                        <el-button size="small" type="primary">
                          {{ refundEdit.refund_evidence_url ? '替换照片' : '点击上传' }}
                        </el-button>
                        <div slot="tip" class="el-upload__tip">只能上传jpg/png文件，且不超过10MB</div>
                      </el-upload>
                    </el-form-item>
                  </el-form>
                </div>
              </template>
            </div>
          </el-card>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getToken } from '@/utils/auth'
import { BASE_URL } from '@/utils/urls'

export default {
  name: 'OrderDetails',
  data() {
    return {
      isMobile: false,
      orderData: {},
      editingPayment: false,
      editingRefund: false,
      editingUserId: false,
      userIdEdit: {
        user_id: '',
      },
      paymentEdit: {
        order_status: 0,
        actual_paid_amount: 0,
        payment_method: 1,
        payment_evidence_url: '',
        transaction_id: '',
        admin_remark: '',
        locked_discount_bonus: 0
      },
      remarkEdit: {
        remark: ''
      },
      refundEdit: {
        refund_amount: 0,
        refund_reason: '',
        refund_evidence_url: ''
      },
      statusMap: {
        0: { type: 'danger', text: '待支付' },
        1: { type: 'info', text: '已取消' },
        2: { type: 'warning', text: '已退款' },
        10: { type: 'warning', text: '待支付定金' },
        11: { type: 'success', text: '已支付定金' },
        20: { type: 'warning', text: '待支付尾款' },
        21: { type: 'success', text: '已支付尾款' },
        30: { type: 'warning', text: '待支付全款' },
        31: { type: 'success', text: '已支付全款' },
        40: { type: 'warning', text: '待支付交通费' },
        41: { type: 'success', text: '已支付交通费' },
        50: { type: 'info', text: '定金已付，待付尾款' },
        51: { type: 'success', text: '已支付定金尾款' },
        100: { type: 'success', text: '交易完成' },
        101: { type: 'warning', text: '部分退款' },
        102: { type: 'warning', text: '全额退款' }
      },
      paymentMethods: {
        1: '微信小程序',
        2: '微信转账',
        3: '支付宝转账',
        4: '扫码支付',
        5: '现金',
        6: '银行卡转账',
        7: '其他'
      },
      creatorInfo: {},
      updaterInfo: {}
    }
  },
  created() {
    this.getOrderDetails()
    this.checkIsMobile()
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async getOrderDetails() {
      try {
        const res = await this.$http.get(`orders/${this.$route.params.id}`)
        this.orderData = res.data
        
        // 获取创建者、更新者和折扣操作人信息
        if (this.orderData.created_by_admin) {
          await this.getUserInfo(this.orderData.created_by_admin, 'creator')
        }
        if (this.orderData.updated_by_admin) {
          await this.getUserInfo(this.orderData.updated_by_admin, 'updater')
        }
      } catch (err) {
        this.$message.error('获取订单详情失败')
      }
    },
    async getUserInfo(userId, type) {
      try {
        const res = await this.$http.get(`users/${userId}`)
        switch (type) {
          case 'creator':
            this.creatorInfo = res.data
            break
          case 'updater':
            this.updaterInfo = res.data
            break
        }
      } catch (err) {
        console.error(`获取${type}信息失败:`, err)
      }
    },
    
    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    },

    getStatusType(status) {
      return this.statusMap[status]?.type || 'info'
    },

    getStatusText(status) {
      return this.statusMap[status]?.text || '未知'
    },

    getPaymentMethod(method) {
      return this.paymentMethods[method] || '未知'
    },

    getPaymentType(type) {
      const paymentTypes = {
        1: '定金',
        2: '尾款', 
        3: '全款',
        4: '交通费'
      }
      return paymentTypes[type] || '未知'
    },


    getTransportationType(type) {
      const transportationTypes = {
        1: '火车',
        2: '飞机',
        3: '单飞',
        4: '自理'
      }
      return transportationTypes[type] || '未选择'
    },

    getImageUrl(path, imageId) {
      this.$http({
        method: 'get',
        url: `images/confidential/${path}`,
        responseType: 'blob'
      }).then(res => {
        let objectURL = URL.createObjectURL(res);
        let img = document.getElementById(imageId);
        img.src = objectURL;
      }).catch(error => {
        console.error(error);
      });
    },

    goBack() {
      this.$router.push('/admin/orders')
    },

    viewActivity(id) {
      this.$router.push(`/admin/activity/details/${id}`)
    },

    goToParticipantDetail() {
      this.$router.push(`/admin/enroll/details/${this.orderData.order_no}`)
    },

    // 支付信息编辑
    startEditPayment() {
      this.editingPayment = true
      this.paymentEdit = {
        order_status: this.orderData.order_status,
        actual_paid_amount: this.orderData.actual_paid_amount || 0,
        payment_method: this.orderData.payment_method || 1,
        payment_evidence_url: this.orderData.payment_evidence_url || '',
        transaction_id: this.orderData.transaction_id || '',
        admin_remark: this.orderData.admin_remark || '',
        locked_discount_bonus: this.orderData.locked_discount_bonus || 0
      }
    },

    async savePayment() {
      try {
        // 规范化与校验折扣金额
        const normalized = Number(this.paymentEdit.locked_discount_bonus)
        this.paymentEdit.locked_discount_bonus = isNaN(normalized) || normalized < 0 ? 0 : Number(normalized.toFixed(2))
        
        await this.$http.put(`orders/${this.$route.params.id}`, this.paymentEdit)
        this.editingPayment = false
        this.getOrderDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '支付信息更新失败')
      }
    },

    cancelEditPayment() {
      this.editingPayment = false
    },

    // 退款信息编辑
    startEditRefund() {
      this.editingRefund = true
      this.refundEdit = {
        refund_amount: this.orderData.refund_amount || 0,
        refund_reason: this.orderData.refund_reason || '',
        refund_evidence_url: this.orderData.refund_evidence_url || ''
      }
    },

    async saveRefund() {
      try {
        await this.$http.put(`orders/${this.$route.params.id}`, this.refundEdit)
        this.editingRefund = false
        this.getOrderDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || '退款信息更新失败')
      }
    },

    cancelEditRefund() {
      this.editingRefund = false
    },

    // UserID编辑
    startEditUserId() {
      this.editingUserId = true
      this.userIdEdit = {
        user_id: this.orderData.user_id || ''
      }
    },

    async saveUserId() {
      try {
        await this.$http.put(`orders/${this.$route.params.id}`, this.userIdEdit)
        this.editingUserId = false
        this.getOrderDetails()
      } catch (err) {
        this.$message.error(err.response?.data?.message || 'UserID更新失败')
      }
    },

    cancelEditUserId() {
      this.editingUserId = false
    },

    getUploadUrl(type) {
      return `${BASE_URL}/api/v1/images/confidential/${type}`
    },

    getHeaders() {
      return {
        'Authorization': `Bearer ${getToken()}`
      }
    },

    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isImage) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('图片大小不能超过 10MB!')
        return false
      }
      return true
    },

    handleUploadSuccess(response, type) {
      if (response.data) {
        if (type === 'payment') {
          this.paymentEdit.payment_evidence_url = response.data
        } else if (type === 'refund') {
          this.refundEdit.refund_evidence_url = response.data
        }
        this.$message.success('上传成功')
      } else {
        this.$message.error('上传失败')
      }
    },

    formatGender(gender) {
      if (gender === 0 || gender === '0') return '男';
      if (gender === 1 || gender === '1') return '女';
      return gender || '-';
    },

    // 获取定金金额
    getDepositAmount() {
      if (!this.orderData.payments) return null;
      const depositPayment = this.orderData.payments.find(p => p.payment_type === 1);
      return depositPayment ? depositPayment.amount : null;
    },

    // 获取尾款金额
    getBalanceAmount() {
      if (!this.orderData.payments) return null;
      const balancePayment = this.orderData.payments.find(p => p.payment_type === 2);
      return balancePayment ? balancePayment.amount : null;
    },

    // 获取交通费金额
    getTransportationAmount() {
      if (!this.orderData.payments) return null;
      const transportationPayment = this.orderData.payments.find(p => p.payment_type === 4);
      return transportationPayment ? transportationPayment.amount : null;
    },

    // 获取最后支付时间
    getLastPaymentTime() {
      if (!this.orderData.payments || this.orderData.payments.length === 0) return null;
      const paidPayments = this.orderData.payments.filter(p => p.paid_at);
      if (paidPayments.length === 0) return null;
      return paidPayments.sort((a, b) => new Date(b.paid_at) - new Date(a.paid_at))[0].paid_at;
    },

    // 获取支付状态类型
    getPaymentStatusType() {
      if (!this.orderData.payments || this.orderData.payments.length === 0) return 'info';
      const paidPayments = this.orderData.payments.filter(p => p.status === 1);
      if (paidPayments.length === this.orderData.payments.length) return 'success';
      if (paidPayments.length > 0) return 'warning';
      return 'danger';
    },

    // 获取支付状态文本
    getPaymentStatusText() {
      if (!this.orderData.payments || this.orderData.payments.length === 0) return '无支付记录';
      const paidPayments = this.orderData.payments.filter(p => p.status === 1);
      if (paidPayments.length === this.orderData.payments.length) return '全部已支付';
      if (paidPayments.length > 0) return '部分已支付';
      return '未支付';
    },

    // 获取交通费支付时间
    getTransportationPaymentTime() {
      if (!this.orderData.payments) return null;
      const transportationPayment = this.orderData.payments.find(p => p.payment_type === 4 && p.paid_at);
      return transportationPayment ? transportationPayment.paid_at : null;
    },

    // 获取交通费支付状态
    getTransportationPaymentStatus() {
      if (!this.orderData.payments) return null;
      const transportationPayment = this.orderData.payments.find(p => p.payment_type === 4);
      if (!transportationPayment) return null;
      return transportationPayment.status === 1 ? '已支付' : '未支付';
    },

    // 获取交通费支付状态类型
    getTransportationPaymentStatusType() {
      if (!this.orderData.payments) return 'info';
      const transportationPayment = this.orderData.payments.find(p => p.payment_type === 4);
      if (!transportationPayment) return 'info';
      return transportationPayment.status === 1 ? 'success' : 'warning';
    },

    // 获取支付记录状态类型
    getPaymentRecordStatusType(status) {
      const statusMap = {
        0: 'danger',   // 未支付
        1: 'success',  // 已支付
        2: 'warning',  // 支付失败
        3: 'info',     // 已退款
        4: 'danger'    // 已关闭
      };
      return statusMap[status] || 'info';
    },

    // 获取支付记录状态文本
    getPaymentRecordStatusText(status, paymentType) {
      // 交通费使用不同的状态文本
      if (paymentType === 4) {
        const transportStatusMap = {
          0: '未设置',
          1: '已设置',
          2: '设置失败',
          3: '已退款',
          4: '已关闭'
        };
        return transportStatusMap[status] || '未知状态';
      }
      
      // 其他支付类型使用原有的状态文本
      const statusMap = {
        0: '未支付',
        1: '已支付',
        2: '支付失败',
        3: '已退款',
        4: '已关闭'
      };
      return statusMap[status] || '未知状态';
    },

    // 格式化价格显示
    formatPrice(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return '¥0.00';
      }
      const num = parseFloat(amount);
      if (isNaN(num)) {
        return '¥0.00';
      }
      return `¥${num.toFixed(2)}`;
    },

    checkIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },

    handleResize() {
      this.isMobile = window.innerWidth <= 768
    }
  }
}
</script>

<style lang="less" scoped>
.order-details {
  min-height: 100vh;
  overflow-x: hidden;
  box-sizing: border-box;

  .content-grid {
    display: grid;
    grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
    gap: 20px;
    margin: 0 auto;

    @media screen and (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .action-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
    margin-bottom: 20px;
    border-radius: 8px;

    .date-info {
      flex: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 48px;
      color: #888;
      font-size: 13px;
    }
  }

  .el-card {
    margin-bottom: 20px;
    border-radius: 8px;

    .card-header {
      position: relative;
      padding: 8px 20px;
      border-radius: 8px;
      background: #fafafa;
      display: flex;
      align-items: center;
      justify-content: space-between;

      span {
        font-size: 14px;
        font-weight: 500;
        color: #1f2937;
        padding-left: 10px;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 12px;
          background: #1890ff;
          border-radius: 1px;
        }
      }

      .header-actions {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;
      }
    }
  }

  .order-info-card {
    .info-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 24px;
      padding: 20px;

      .info-section {
        display: flex;
        flex-direction: column;
        gap: 16px;

        .info-item {
          background: #fafafa;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;

          .label {
            display: block;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
            
          }

          .value {
            font-size: 14px;
            color: #262626;

            &.amount {
              color: #f56c6c;
              font-size: 20px;
              font-weight: 600;
              font-family: DIN Alternate, -apple-system, BlinkMacSystemFont, sans-serif;
              
              &::before {
                content: '¥';
                font-size: 16px;
                margin-right: 2px;
                font-weight: normal;
              }
            }

            &.price {
              color: #f56c6c;
              font-size: 16px;
              font-weight: 500;
            }

            &.paid {
              color: #52c41a;
              font-weight: 600;
            }
            
            &.discount {
              color: #722ed1;
              font-weight: 600;
            }
            
            &.payable {
              color: #fa8c16;
              font-weight: 600;
            }
            
            &.refund {
              color: #ff4d4f;
              font-weight: 600;
            }
          }
        }
      }
    }
  }

  .activity-info-card {
    margin-bottom: 20px;
    .card-header {
      position: relative;
      padding: 8px 20px;
      border-radius: 8px;
      background: #fafafa;
      display: flex;
      align-items: center;
      justify-content: space-between;
      span {
        font-size: 14px;
        font-weight: 500;
        color: #1f2937;
        padding-left: 10px;
        position: relative;
        &::before {
          content: '';
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          width: 2px;
          height: 12px;
          background: #1890ff;
          border-radius: 1px;
        }
      }
      .header-actions {
        position: absolute;
        right: 16px;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;
      }
    }
    .activity-participants {
      padding: 20px;
      
    .desc-participant-card {
      min-width: 200px;
      border-radius: 8px;
      background: #fff;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
      transition: all 0.3s ease;
      cursor: pointer;
    }

    .desc-participant-card:hover {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
      border-color: #409eff;
    }
      .desc-title {
        display: flex;
        align-items: center;
        font-weight: bold;
        font-size: 14px;
        margin-bottom: 12px;
        color: #333;
        letter-spacing: 1px;
      }

      .desc-title span:hover {
        text-decoration: underline;
      }
      .desc-title-bar {
        display: inline-block;
        width: 3px;
        height: 16px;
        background: #409EFF;
        border-radius: 2px;
        margin-right: 10px;
      }
      .desc-unfilled {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #d46b08;
        background: #fff7e6;
        border-radius: 6px;
        font-size: 14px;
        font-weight: 500;
        padding: 18px 0;
        margin: 8px 0;
        letter-spacing: 1px;
      }
      .desc-unfilled .el-icon-warning-outline {
        font-size: 16px;
        margin-right: 8px;
      }
    }
  }

  // 费用概述卡片
  .cost-overview-card {
    margin-bottom: 20px;
    
    .cost-overview {
      .cost-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        padding: 20px;

        .cost-item {
          background: #fafafa;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;

          .label {
            display: block;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
            
            .label-note {
              font-size: 11px;
              color: #999;
              font-weight: normal;
            }
          }

          .value {
            font-size: 14px;
            color: #262626;

            &.total {
              color: #409eff;
              font-weight: 600;
            }

            &.paid {
              color: #52c41a;
              font-weight: 600;
            }
            
            &.discount {
              color: #722ed1;
              font-weight: 600;
            }
            
            &.payable {
              color: #fa8c16;
              font-weight: 600;
            }
            
            &.balance {
              color: #909399;
            }
            
            &.refund {
              color: #ff4d4f;
              font-weight: 600;
            }
          }
        }
      }
    }
  }

  // 费用卡片统一样式
  .fee-card {
    margin-bottom: 16px;
    
    .fee-content {
      .fee-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        padding: 20px;

        .fee-item {
          background: #fafafa;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;

          .label {
            display: block;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
            
          }

          .value {
            font-size: 14px;
            color: #262626;

            &.paid {
              color: #52c41a;
              font-weight: 600;
            }
            
            &.discount {
              color: #722ed1;
              font-weight: 600;
            }
            
            &.payable {
              color: #fa8c16;
              font-weight: 600;
            }
            
            &.refund {
              color: #ff4d4f;
              font-weight: 600;
            }
          }
        }
      }
    }
  }


  // 支付凭证卡片
  .payment-evidence-card {
    margin-bottom: 20px;
    
    .evidence-content {
      .evidence-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        padding: 20px;

        .evidence-item {
          background: #fafafa;
          padding: 12px 16px;
          border-radius: 8px;
          border: 1px solid #f0f0f0;

          &.full-width {
            grid-column: 1 / -1;
          }

          .label {
            display: block;
            font-size: 13px;
            color: #666;
            margin-bottom: 8px;
            
          }

          .value {
            font-size: 14px;
            color: #262626;
            word-break: break-all;
          }
          
          .evidence-image {
            margin-top: 8px;
            
            img {
              width: 100%;
              height: 120px;
              border-radius: 8px;
              object-fit: cover;
              border: 1px solid #e6e6e6;
            }
          }
        }
      }
    }
  }

  .payment-card,
  .refund-card {
    .payment-grid,
    .refund-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      padding: 20px;

      .payment-item,
      .refund-item {
        background: #fafafa;
        padding: 12px 16px;
        border-radius: 8px;
        border: 1px solid #f0f0f0;

        &.full-width {
          grid-column: 1 / -1;
        }

        .label {
          display: block;
          font-size: 13px;
          color: #666;
          margin-bottom: 8px;
        }

        .value {
          font-size: 14px;
          color: #262626;

          &.price {
            color: #f56c6c;
            font-size: 16px;
            font-weight: 500;
          }
        }

        .evidence-image {
          margin-top: 8px;

          img {
            width: 100%;
            height: 120px;
            border-radius: 4px;
            object-fit: cover;
            overflow: hidden;
            display: inline-block;
            background-color: #F5F7FA;
            color: #C0C4CC;
            text-align: center;
            line-height: 120px;
            font-size: 14px;
          }
        }
      }
    }

    .payment-form,
    .refund-form {
      padding: 20px;

      .el-form-item {
        margin-bottom: 20px;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .remark-card {
    .remark-content {
      padding: 20px;

      .remark-text {
        background: #fafafa;
        padding: 12px 16px;
        border-radius: 8px;
        border: 1px solid #f0f0f0;
        min-height: 90px;
        font-size: 14px;
        color: #666;
        line-height: 1.6;
      }
    }
  }

  // 支付记录卡片
  .payment-records-card {
    margin-bottom: 20px;
    
    .payment-records-content {
      padding: 16px;
      
      .payment-record {
        background: #fff;
        border-radius: 6px;
        border: 1px solid #e8e8e8;
        margin-bottom: 12px;
        transition: all 0.2s ease;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        &:hover {
          border-color: #d9d9d9;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
        }
        
        .record-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          
          .record-title {
            display: flex;
            align-items: center;
            gap: 8px;
            
            .record-number {
          font-size: 14px;
              font-weight: 500;
          color: #262626;
            }
          }

          .record-amount {
            .amount-value {
              font-size: 15px;
              font-weight: 600;
              color: #f56c6c;
            }
            
            .no-payment-container {
              display: inline-flex;
              align-items: center;
              padding: 2px 8px;
              background: #f8f9fa;
              border: 1px solid #e9ecef;
              border-radius: 4px;
              
              .no-payment-text {
                font-size: 12px;
                font-weight: 400;
                color: #6c757d;
                letter-spacing: 0.5px;
              }
            }
          }
        }
        
        .record-details {
          padding: 0 16px 12px;
          border-top: 1px solid #f5f5f5;
          margin-top: 8px;
          padding-top: 12px;
          
          .detail-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 16px;
            
            .detail-item {
              flex: 1;
              min-width: 120px;
              
              .label {
                display: block;
                font-size: 11px;
                color: #8c8c8c;
                margin-bottom: 2px;
                text-transform: uppercase;
                letter-spacing: 0.5px;
              }
              
              .value {
                font-size: 12px;
                color: #595959;
                word-break: break-all;
                
                &.error {
                  color: #f56c6c;
                }
              }
            }
          }
        }
      }
    }
  }

}

.el-button {
  &--text {
    padding: 4px 8px;
    font-size: 13px;

    &:hover {
      background: rgba(24, 144, 255, 0.06);
      color: #1890ff;
    }
  }
}

.evidence-preview {
  margin-bottom: 12px;
  
  img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    border-radius: 4px;
    border: 1px solid #eee;
  }
}

.evidence-uploader {
  :deep(.el-upload__tip) {
    font-size: 12px;
    color: #666;
    margin-top: 8px;
  }
}

.el-textarea :deep(.el-textarea__inner) {
  font-family: inherit;
  padding: 12px 16px;
  border-radius: 8px;
  line-height: 1.6;
}

.right-actions {
  .user-id-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    
    .el-tag {
      margin-right: 4px;
      cursor: pointer;
      transition: all 0.3s;
      
      &:hover {
        border-color: #409eff;
      }
    }
    
    .user-id-input {
      width: 200px;
      
      :deep(.el-input-group__append) {
        padding: 0;
        
        .el-button {
          border: none;
          margin: 0;
          padding: 8px 12px;
          
          &:first-child {
            border-right: 1px solid #dcdfe6;
          }
          
          &:hover {
            color: #409eff;
          }
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .order-details {
    padding: 0 5px;
  }

  .action-bar {
    flex-wrap: wrap;
    gap: 10px;
    align-items: center;
    padding: 10px !important;
    margin-bottom: 0px;

    .left-actions {
      .el-button {
        padding: 8px 10px;
        font-size: 10px;
      }
    }

    .date-info {
      gap: 2px;
      color: #909399;
      font-size: 10px !important;
      width: 100%;
    }

    .right-actions {
      flex-shrink: 0;
      margin: 0 auto;

      .el-tag {
        font-size: 10px !important;
        height: 24px;
        line-height: 24px;
      }
    }
  }

  .content-grid {
    grid-template-columns: 1fr !important;
    gap: 12px;
    min-width: 0;

    .left-column,
    .right-column {
      min-width: 0;
    }
  }

  .el-card {
    margin-bottom: 12px !important;
    padding: 10px;
    overflow: hidden;
    min-width: 0;
  }

  .card-header {
    padding: 8px 12px !important;
  }

  .order-info-card .info-grid {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 10px !important;
    padding: 12px !important;
  }

  .cost-overview .cost-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 10px !important;
    padding: 12px !important;
  }

  .refund-grid {
    grid-template-columns: 1fr !important;
    gap: 10px !important;
    padding: 12px !important;
  }

  .info-item,
  .cost-item,
  .refund-item {
    padding: 10px 12px !important;
  }

  .info-item .label,
  .cost-item .label,
  .refund-item .label {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .info-item .value,
  .cost-item .value,
  .refund-item .value {
    font-size: 13px;
  }

  .info-item .value.amount {
    font-size: 18px;
  }

  .desc-participant-card {
    padding: 12px;
    min-width: 0;
    overflow: hidden;
  }

  .desc-title {
    font-size: 14px;
    margin-bottom: 12px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f0f0;
  }

  .el-descriptions {
    font-size: 13px;
    width: 100%;
  }

  .el-descriptions-item__label {
    font-size: 12px;
    padding: 10px 8px !important;
    white-space: nowrap;
    width: auto;
    flex-shrink: 0;
  }

  .el-descriptions-item__content {
    font-size: 13px;
    padding: 10px 8px !important;
    word-break: break-all;
  }

  .payment-records-content {
    padding: 10px;
  }

  .payment-record {
    padding: 10px;
    margin-bottom: 8px;
  }

  .record-header {
    padding: 10px !important;
  }

  .record-details {
    padding: 0 10px 10px !important;
  }

  .detail-grid {
    flex-direction: column;
    gap: 8px;
  }

  .detail-item {
    min-width: 100%;
  }

  .payment-form,
  .refund-form {
    padding: 12px;
  }

  .el-form-item {
    margin-bottom: 16px !important;
  }

  .el-form-item__label {
    font-size: 12px;
    width: 80px !important;
  }

  .el-input__inner {
    font-size: 13px;
    height: 34px;
  }

  .el-select .el-input__inner {
    font-size: 13px;
  }

  .el-input-number {
    font-size: 13px;
  }

  .el-dialog {
    width: 95% !important;
    margin: 0 auto;
    top: 5vh;
  }

  .evidence-preview img {
    height: 100px;
  }

  .evidence-image img {
    height: 100px;
  }
}
</style>

